import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { message, notification } from 'antd';
import { getApiUrl } from '@/utils/api';
import { getToken } from '@/utils/auth';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  code: number;
  msg: string;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      if (res instanceof Blob) return;
      const { code, msg, data, showType } = res as unknown as ResponseStructure;
      if (code !== 200) {
        const error: any = new Error(msg);
        error.name = 'BizError';
        error.info = { errorCode: code, errorMessage: msg, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 自定义：允许通过 options.showError=false 关闭错误提示
      const shouldShowError = opts?.showError !== false;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              shouldShowError && message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              shouldShowError && message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              shouldShowError &&
                notification.open({
                  description: errorMessage,
                  message: errorCode,
                });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              shouldShowError && message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // Axios 的错误
        // 请求成功发出且服务器也响应了状态码，但状态代码超出了 2xx 的范围
        shouldShowError &&
          message.error(`Response status:${error.response.status}`);
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        shouldShowError && message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        shouldShowError && message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      if (config.url) config.url = getApiUrl(config.url);
      // 拦截请求配置，进行个性化处理。
      // 从 localStorage 获取 token 并添加到请求中
      const token = getToken();
      if (token) {
        // 方式1: 通过 URL 参数传递 token
        // const url = config?.url?.concat(config?.url?.includes('?') ? '&' : '?', `token=${token}`);
        // return { ...config, url };

        // 方式2: 通过请求头传递 token (推荐)
        config.headers = {
          ...config.headers,
          Authorization: `${token}`,
          // 或者使用其他字段名，根据后端要求调整
          // token: token,
        };
      }
      return config;
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      if (response.data instanceof Blob) return response;
      // 拦截响应数据，进行个性化处理
      const { data } = response as unknown as { data: ResponseStructure };
      // 从请求配置读取 showError（默认 true）
      const req: any = response?.config || (response as any)?.request?.options;
      const shouldShowError = req?.showError !== false;

      if (data?.code !== 200) {
        shouldShowError && message.error(data.msg || '请求失败！');

        if (data?.code === 400) {
          // 登录失效
          history.push('/user/login');
        }
      }

      return response;
    },
  ],
};
